import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Divider, Select, Tooltip } from '@mantine/core';
import { triggerNotification } from '../../../helpers/notificationHelper';
import AppCard from '../../common/AppCard';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { useMediaQueries } from '../../../helpers/hooks';
import CheckoutOrderAddressForm from './CheckoutOrderAddressForm';
import CheckoutOrderSummaryPriceBreakdown from './CheckoutOrderSummaryPriceBreakdown';
import { useRegCartController } from '../../../helpers/shop/useRegCartController';
import WsFinalSaleWarning from '../ecommerce/webstore/blocks/finalSaleWarning/WsFinalSaleWarning';
import { getCheckoutPaymentPlanInfo } from '../../../helpers/installmentsHelper';
import CheckoutPaymentPlanSelectModal from './CheckoutPaymentPlanSelectModal';

const CHECKOUT_PAYMENT_TYPES = {
  CARD: 'card',
  INSTALLMENTS: 'installments',
  OFFLINE: 'offline'
};

const CheckoutOrderSummary = ({
  loading,
  regCart,
  regAssociation,
  onCheckout,
  isCheckoutSubmitting,
  checkoutPaymentType,
  ecomStoreRosterPlayers,
  onFundraiserChange,
  fundraiser,
  hasFinalSaleProducts
}) => {
  const { isTabletOrSmaller } = useMediaQueries();
  const { cartShippingController } = useRegCartController({fetchShippingOnAddressChange: true});
  const [isPaymentPlanModalOpen, setIsPaymentPlanModalOpen] = useState(false);

  const { shippingLoading, onChangeShippingAddress } = cartShippingController;

  const offlinePaymentsDisabled =
    regCart?.regCartEcomStoreProductVariants.length > 0 ||
    regCart?.regCartMerchPackages.length > 0 ||
    regCart?.regCartProducts.some(
      (f) =>
        !f.regFormSubmission.regAssociationDivisionForm.regAssociationDivision
          .offlinePaymentsEnabled
    );

  const currency = regCart?.cart.currency?.toUpperCase() ?? 'CAD';
  const checkoutCart = regCart?.cart;
  const orderSummaryLoading = loading || shippingLoading;

  const availableCheckoutPaymentPlans =
    checkoutCart?.paymentPlans.filter(
      (p) =>
        (!p.definition.maximumAllowedTransactionTotal ||
          p.definition.maximumAllowedTransactionTotal >=
            checkoutCart.checkoutTotalInCents) &&
        !p.isUnavailable
    ) ?? [];
  const singlePaymentPlanOption =
    availableCheckoutPaymentPlans.length === 1
      ? availableCheckoutPaymentPlans[0]
      : null;
  const singlePaymentPlanOptionInfo = singlePaymentPlanOption
    ? getCheckoutPaymentPlanInfo(singlePaymentPlanOption)
    : null;

  return (
    <AppCard
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        const { submitter } = e.nativeEvent;
        const requestedPaymentType = submitter.dataset.payment;

        if (
          requestedPaymentType === CHECKOUT_PAYMENT_TYPES.OFFLINE &&
          offlinePaymentsDisabled
        ) {
          triggerNotification(
            'Offline payments are not supported for this order.'
          );
          return;
        }

        if (requestedPaymentType !== CHECKOUT_PAYMENT_TYPES.INSTALLMENTS) {
          onCheckout(requestedPaymentType);
        }
        else if (singlePaymentPlanOption) {
          onCheckout(
            requestedPaymentType,
            singlePaymentPlanOption.definition.id
          );
        }
        else {
          setIsPaymentPlanModalOpen(true);
        }
      }}
      shadow="lg"
      style={{
        flex: 1,
        padding: 0,
        borderRadius: isTabletOrSmaller ? 0 : 8,
        width: '100%'
      }}
      withBorder={!isTabletOrSmaller}
    >
      {((checkoutCart && !checkoutCart.freeShipping) ||
        (ecomStoreRosterPlayers && ecomStoreRosterPlayers.length > 0)) && (
        <>
          <AppStack style={{ gap: 8, margin: 20 }}>
            {checkoutCart && !checkoutCart.freeShipping && (
              <CheckoutOrderAddressForm
                checkoutCart={regCart}
                disabled={isCheckoutSubmitting || loading}
                loading={loading}
                loadingCheckout={isCheckoutSubmitting}
                shippingController={cartShippingController}
              />
            )}

            {ecomStoreRosterPlayers && ecomStoreRosterPlayers.length > 0 && (
              <Select
                clearable
                data={ecomStoreRosterPlayers.map((p) => ({
                  label: p.name,
                  value: p.pkEcomStoreRosterPlayer.toString()
                }))}
                description="Select the fundraiser to credit for this order."
                disabled={orderSummaryLoading}
                label="Fundraiser"
                onChange={(v) => {
                  onFundraiserChange({ fkEcomStoreRosterPlayer: v });
                }}
                searchable
                value={fundraiser?.fkEcomStoreRosterPlayer || null}
              />
            )}
          </AppStack>
          <Divider />
        </>
      )}

      <CheckoutOrderSummaryPriceBreakdown
        loading={
          (!regCart || regCart.cart.lineItems.length !== 0) &&
          orderSummaryLoading
        }
        onToggleShippingChange={onChangeShippingAddress}
        regCart={regCart}
      />

      <AppStack
        style={{
          backgroundColor: '#000',
          color: '#FFF',
          opacity: 0.8,
          padding: '20px 20px'
        }}
      >
        <AppStack style={{ gap: 20 }}>
          {hasFinalSaleProducts && <WsFinalSaleWarning isCheckout />}
          <AppStack style={{ gap: 25 }}>
            {regAssociation && (
              <Checkbox
                disabled={isCheckoutSubmitting || loading}
                label={`Subscribe to receive emails from ${regAssociation.association.name}`}
                required
                styles={{ label: { fontWeight: 500 } }}
              />
            )}

            <Button
              data-payment={CHECKOUT_PAYMENT_TYPES.CARD}
              disabled={
                !regCart ||
                regCart.cart.lineItems.length === 0 ||
                loading ||
                (isCheckoutSubmitting &&
                  checkoutPaymentType !== CHECKOUT_PAYMENT_TYPES.CARD)
              }
              loading={
                isCheckoutSubmitting &&
                checkoutPaymentType === CHECKOUT_PAYMENT_TYPES.CARD
              }
              size="lg"
              type="submit"
            >
              Continue to Checkout
            </Button>

            {checkoutCart && (
              <>
                {availableCheckoutPaymentPlans.length > 0 && (
                  <>
                    <Divider
                      color="lightgrey"
                      label="OR"
                      labelPosition="center"
                    />

                    <AppStack style={{ gap: 10 }}>
                      <Button
                        data-payment={CHECKOUT_PAYMENT_TYPES.INSTALLMENTS}
                        disabled={
                          loading ||
                          (isCheckoutSubmitting &&
                            checkoutPaymentType !==
                              CHECKOUT_PAYMENT_TYPES.INSTALLMENTS)
                        }
                        loading={
                          isCheckoutSubmitting &&
                          checkoutPaymentType ===
                            CHECKOUT_PAYMENT_TYPES.INSTALLMENTS
                        }
                        size="lg"
                        type="submit"
                      >
                        {singlePaymentPlanOption
                          ? `${singlePaymentPlanOptionInfo.paymentPlanLabel} ${currency}*`
                          : 'Choose a Payment Plan'}
                      </Button>

                      {singlePaymentPlanOption &&
                        singlePaymentPlanOptionInfo.serviceFeeLabel && (
                          <AppText
                            style={{
                              fontSize: 13,
                              color: 'lightgrey',
                              textAlign: 'center'
                            }}
                          >
                            This includes an additional{' '}
                            {singlePaymentPlanOptionInfo.serviceFeeLabel} fee
                            for installment payments.
                          </AppText>
                        )}
                    </AppStack>
                  </>
                )}

                {checkoutCart.offlinePaymentsEnabled && (
                  <>
                    <Divider
                      color="lightgrey"
                      label="OR"
                      labelPosition="center"
                    />
                    <Tooltip
                      disabled={!offlinePaymentsDisabled}
                      label="Offline payments disabled because not all items in your cart support them"
                      multiline
                      style={{ display: 'flex' }}
                      width={250}
                      withArrow
                      withinPortal
                    >
                      <Button
                        data-payment={CHECKOUT_PAYMENT_TYPES.OFFLINE}
                        disabled={
                          loading ||
                          (isCheckoutSubmitting &&
                            checkoutPaymentType !==
                              CHECKOUT_PAYMENT_TYPES.OFFLINE)
                        }
                        loading={
                          isCheckoutSubmitting &&
                          checkoutPaymentType === CHECKOUT_PAYMENT_TYPES.OFFLINE
                        }
                        size="lg"
                        type="submit"
                      >
                        Cash / Cheque Payment
                      </Button>
                    </Tooltip>
                  </>
                )}
              </>
            )}
          </AppStack>
        </AppStack>
      </AppStack>

      <CheckoutPaymentPlanSelectModal
        isOpen={isPaymentPlanModalOpen}
        onClose={() => setIsPaymentPlanModalOpen(false)}
        onContinue={({ paymentPlanId }) => {
          onCheckout(CHECKOUT_PAYMENT_TYPES.INSTALLMENTS, paymentPlanId);
          setIsPaymentPlanModalOpen(false);
        }}
        paymentPlans={availableCheckoutPaymentPlans}
      />
    </AppCard>
  );
};

CheckoutOrderSummary.propTypes = {
  checkoutPaymentType: PropTypes.string,
  ecomStoreRosterPlayers: PropTypes.array,
  fundraiser: PropTypes.object,
  hasFinalSaleProducts: PropTypes.bool,
  isCheckoutSubmitting: PropTypes.bool,
  loading: PropTypes.bool,
  onCheckout: PropTypes.func,
  onFundraiserChange: PropTypes.func,
  regAssociation: PropTypes.object,
  regCart: PropTypes.object
};

export default CheckoutOrderSummary;
